import React, { useState, useEffect, useRef } from 'react';
import {
  createTheme,
  ThemeProvider,
  CssBaseline,
  Container,
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  TextField,
  FormControlLabel,
  Switch
} from '@mui/material';
import PaymentForm from './PaymentForm';
import useApi from '../Utils/useApi';

const theme = createTheme({
  typography: {
    htmlFontSize: 20,
    h1: {
      fontSize: 'clamp(2rem, 5vw, 3rem)',
      fontWeight: 'bold',
    },
    h5: {
      fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
      fontWeight: 'bold',
    },
    body1: {
      fontSize: '1.1rem',
    },
  },
});

const Checkout = () => {
  const [students, setStudents] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  const [selectedNumberOfStudents, setSelectedNumberOfStudents] = useState('1 Student');
  const [selectedSubscription, setSelectedSubscription] = useState('Month');
  const [selectedPrice, setSelectedPrice] = useState(0);
  const [discountedPrice, setDiscountedPrice] = useState(0);
  const [requestReference, setRequestReference] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [isValidCode, setIsValidCode] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [originalPrice, setOriginalPrice] = useState(0);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountDescription, setDiscountDescription] = useState('');
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const api = useApi();
  const isFetched = useRef(false);

  const fixedPrices = {
    '1 Student': { Month: 20, Year: 149 },
    '2 Students': { Month: 30, Year: 179 },
    'Up to 5 Students': { Month: 40, Year: 199 },
  };

  const studentOptions = [
    { label: 'Single Student', id: '1 Student' },
    { label: 'Duo', id: '2 Students' },
    { label: 'Family Pack', id: 'Up to 5 Students' },
  ];

  useEffect(() => {
    const fetchStudents = async () => {
      const parent_id = localStorage.getItem('user_id');
      if (!parent_id) return;
      try {
        const res = await api.get(`/user/students/${parent_id}`);
        setStudents(res.data);
        isFetched.current = true;
      } catch (err) {
        console.error('Error fetching students:', err);
      }
    };

    if (!isFetched.current) fetchStudents();
  }, [api]);

  useEffect(() => {
    const prices = fixedPrices[selectedNumberOfStudents];
    const price = selectedSubscription === 'Month' ? prices.Month : prices.Year;
    setSelectedPrice(price);
    setOriginalPrice(price);

    // If discount code is applied, re-validate to recalculate for new price
    if (isValidCode && discountCode) {
      revalidateDiscount(price);
    } else {
      setDiscountedPrice(price);
    }
  }, [selectedNumberOfStudents, selectedSubscription]);

  const getStudentLimit = () => {
    return {
      '1 Student': 1,
      '2 Students': 2,
      'Up to 5 Students': 5,
    }[selectedNumberOfStudents] || Infinity;
  };

  const handleStudentToggle = (id) => {
    const newSelection = new Set(selectedStudents);
    newSelection.has(id) ? newSelection.delete(id) : newSelection.size < getStudentLimit() && newSelection.add(id);
    setSelectedStudents(newSelection);
  };

  const handleApplyDiscount = async () => {
    const code = discountCode.trim().toUpperCase();
    if (!code) {
      alert('Please enter a discount code.');
      return;
    }

    setIsValidatingCode(true);

    try {
      const res = await api.post('/payment/validate-discount-code', {
        code: code,
        original_amount: selectedPrice,
        subscription_type: selectedSubscription
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (res.data.valid) {
        // Discount is valid
        setIsValidCode(true);
        setOriginalPrice(res.data.original_amount);
        setDiscountAmount(res.data.discount_amount);
        setDiscountedPrice(res.data.final_amount);
        setDiscountDescription(res.data.description || `${res.data.discount_percent}% off`);
        setDiscountCode(res.data.code); // Use the uppercase version from backend
      } else {
        // Should not reach here as backend returns 400/404 for invalid codes
        alert(res.data.message || 'Invalid discount code.');
        setIsValidCode(false);
        setDiscountedPrice(selectedPrice);
      }
    } catch (err) {
      console.error('Error validating discount code:', err);
      const errorMessage = err.response?.data?.message || 'Invalid discount code.';
      alert(errorMessage);
      setIsValidCode(false);
      setDiscountedPrice(selectedPrice);
    } finally {
      setIsValidatingCode(false);
    }
  };

  // Revalidate discount when subscription type or student count changes
  const revalidateDiscount = async (newPrice) => {
    try {
      const res = await api.post('/payment/validate-discount-code', {
        code: discountCode,
        original_amount: newPrice,
        subscription_type: selectedSubscription
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` }
      });

      if (res.data.valid) {
        setOriginalPrice(res.data.original_amount);
        setDiscountAmount(res.data.discount_amount);
        setDiscountedPrice(res.data.final_amount);
        setDiscountDescription(res.data.description || `${res.data.discount_percent}% off`);
      } else {
        // If discount is no longer valid (e.g., wrong subscription type), reset
        setIsValidCode(false);
        setDiscountedPrice(newPrice);
        setDiscountAmount(0);
        alert('Discount code is not valid for this subscription type.');
      }
    } catch (err) {
      console.error('Error revalidating discount code:', err);
      // Keep the discount invalid if revalidation fails
      setIsValidCode(false);
      setDiscountedPrice(newPrice);
      setDiscountAmount(0);
    }
  };

  const handleBuyNow = async () => {
    const selectedIds = Array.from(selectedStudents);
    if (!selectedSubscription || selectedIds.length === 0) {
      alert('Please complete all selections.');
      return;
    }
    try {
      const postData = {
        child_ids: selectedIds.join(','),
        subscription_type: selectedSubscription,
        amount: discountedPrice,
        original_amount: originalPrice || selectedPrice, // Always send full price
        discount_code: isValidCode ? discountCode : null, // Only send code if valid
        auto_renew: autoRenew,
      };
      const res = await api.post('/payment/pending-subscriptions', postData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setRequestReference(res.data.request_reference || '');
    } catch (err) {
      console.error('Error processing checkout:', err);
      alert('Checkout failed.');
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #4a91e3 0%, #4a91e3 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 5
      }}>
        <Container maxWidth="sm">
          <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h4" align="center" gutterBottom>
              Rocket Learning Checkout
            </Typography>

            <Typography variant="h6" sx={{ mt: 3 }}>Choose Package</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {studentOptions.map((opt) => (
                <Grid item xs={12} sm={4} key={opt.id}>
                  <Button
                    fullWidth
                    variant={selectedNumberOfStudents === opt.id ? 'contained' : 'outlined'}
                    onClick={() => setSelectedNumberOfStudents(opt.id)}
                  >
                    {opt.label}
                  </Button>
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={{ mt: 3 }}>Subscription Length</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {['Month', 'Year'].map((term) => (
                <Grid item xs={6} key={term}>
                  <Button
                    fullWidth
                    variant={selectedSubscription === term ? 'contained' : 'outlined'}
                    onClick={() => setSelectedSubscription(term)}
                  >
                    {term} (£{fixedPrices[selectedNumberOfStudents][term]})
                  </Button>
                </Grid>
              ))}
            </Grid>

            <Typography variant="h6" sx={{ mt: 3 }}>Select Students</Typography>
            <Grid container spacing={1} sx={{ mt: 1 }}>
              {students.map((s) => (
                <Grid item xs={6} key={s.id}>
                  <Button
                    fullWidth
                    variant={selectedStudents.has(s.id) ? 'contained' : 'outlined'}
                    onClick={() => handleStudentToggle(s.id)}
                  >
                    {s.firstname} {s.lastname}
                  </Button>
                </Grid>
              ))}
            </Grid>

            {/* 🔁 Auto-Renew Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={autoRenew}
                  onChange={() => setAutoRenew(!autoRenew)}
                  color="primary"
                />
              }
              label="Auto-renew subscription (cancel anytime)"
              sx={{ mt: 3 }}
            />

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Discount Code"
                value={discountCode}
                onChange={(e) => setDiscountCode(e.target.value)}
                disabled={isValidatingCode}
              />
              <Button
                variant="contained"
                onClick={handleApplyDiscount}
                disabled={!discountCode.trim() || isValidatingCode}
              >
                {isValidatingCode ? 'Checking...' : 'Apply'}
              </Button>
            </Box>

            {isValidCode && (
              <Typography variant="body2" color="green" sx={{ mt: 1 }}>
                🎉 Discount applied! {discountDescription} - Save £{discountAmount.toFixed(2)}
              </Typography>
            )}

            <Box sx={{ mt: 3 }}>
              {isValidCode && originalPrice > discountedPrice ? (
                <>
                  <Typography variant="body2" sx={{ textDecoration: 'line-through', color: 'gray' }}>
                    Original: £{originalPrice.toFixed(2)}
                  </Typography>
                  <Typography variant="h6" color="primary">
                    Total: £{discountedPrice.toFixed(2)}
                  </Typography>
                </>
              ) : (
                <Typography variant="h6">Total: £{discountedPrice.toFixed(2)}</Typography>
              )}
            </Box>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{ mt: 3 }}
              onClick={handleBuyNow}
              disabled={selectedStudents.size === 0 || !selectedSubscription}
            >
              Buy Now
            </Button>

            {requestReference && (
              <Box sx={{ mt: 4 }}>
                <PaymentForm
                  domain="https://payments.securetrading.net"
                  amount={discountedPrice}
                  currency="GBP"
                  requestReference={requestReference}
                  autoRenew={autoRenew}
                />
              </Box>
            )}
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default Checkout;
